import React, { useMemo } from 'react';
import { CompleteKundali, Language, PlanetPosition, PlanetName } from '../../types/kundali';
import { 
  Compass, 
  Sparkles, 
  Zap, 
  Target, 
  ExternalLink, 
  Clock, 
  ShieldCheck, 
  RotateCcw,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { calculateLivePlanetaryEphemeris, ZODIAC_SIGNS, calculateDoubleTransit } from '../../utils/vedicEngine';
import { ZODIAC_HI, getPlanetShortCode } from '../../utils/language';

interface GocharQuickGlanceProps {
  kundali: CompleteKundali;
  lang?: Language;
  targetAnalysisDateTime: Date;
  onNavigateToGochar?: () => void;
  highlightedPlanet?: string | null;
  onSelectPlanet?: (planetName: string) => void;
}

export const GocharQuickGlance: React.FC<GocharQuickGlanceProps> = ({
  kundali,
  lang = 'en',
  targetAnalysisDateTime,
  onNavigateToGochar,
  highlightedPlanet,
  onSelectPlanet
}) => {
  const isHi = lang === 'hi';
  const { houses, planets } = kundali;

  const lagnaHouse = houses[0];
  const lagnaSignIndex = lagnaHouse ? lagnaHouse.signIndex : 0;
  const lagnaSign = lagnaHouse?.sign || 'Aries';

  const moonPlanet = planets.find(p => p.name === 'Moon');
  const moonSignIndex = moonPlanet ? moonPlanet.signIndex : 0;
  const moonSign = moonPlanet?.sign || 'Aries';

  // Live ephemeris at the shared analytical moment
  const liveEphemeris = useMemo(() => {
    return calculateLivePlanetaryEphemeris(targetAnalysisDateTime);
  }, [targetAnalysisDateTime]);

  // House distance calculator (1-indexed)
  const calcHouse = (refSignIdx: number, transitSignIdx: number) => {
    let dist = transitSignIdx - refSignIdx + 1;
    if (dist <= 0) dist += 12;
    return dist;
  };

  const jupiterTransit = liveEphemeris.find(p => p.planet === 'Jupiter');
  const saturnTransit = liveEphemeris.find(p => p.planet === 'Saturn');
  const rahuTransit = liveEphemeris.find(p => p.planet === 'Rahu');
  const ketuTransit = liveEphemeris.find(p => p.planet === 'Ketu');

  // Authoritative Double Transit calculation from live transits
  const doubleTransitHouses = useMemo(() => {
    return calculateDoubleTransit(lagnaSignIndex, targetAnalysisDateTime);
  }, [lagnaSignIndex, targetAnalysisDateTime]);

  // Aspect helper for factual summary
  const getAspectHouses = (planetName: string, placedHouse: number): number[] => {
    const aspects: number[] = [((placedHouse - 1 + 6) % 12) + 1]; // 7th aspect
    if (planetName === 'Jupiter') {
      aspects.push(((placedHouse - 1 + 4) % 12) + 1); // 5th
      aspects.push(((placedHouse - 1 + 8) % 12) + 1); // 9th
    } else if (planetName === 'Saturn') {
      aspects.push(((placedHouse - 1 + 2) % 12) + 1); // 3rd
      aspects.push(((placedHouse - 1 + 9) % 12) + 1); // 10th
    }
    return Array.from(new Set(aspects)).sort((a, b) => a - b);
  };

  const jupHouseLagna = jupiterTransit ? calcHouse(lagnaSignIndex, jupiterTransit.signIndex) : 1;
  const jupHouseMoon = jupiterTransit ? calcHouse(moonSignIndex, jupiterTransit.signIndex) : 1;
  const jupAspects = jupiterTransit ? getAspectHouses('Jupiter', jupHouseLagna) : [];

  const satHouseLagna = saturnTransit ? calcHouse(lagnaSignIndex, saturnTransit.signIndex) : 1;
  const satHouseMoon = saturnTransit ? calcHouse(moonSignIndex, saturnTransit.signIndex) : 1;
  const satAspects = saturnTransit ? getAspectHouses('Saturn', satHouseLagna) : [];

  // Sade Sati / Dhaiyya status
  const sadeSatiPhase = useMemo(() => {
    if (!saturnTransit) return null;
    const houseFromMoon = satHouseMoon;
    if (houseFromMoon === 12) return { name: 'Sade Sati (1st Phase / Rising)', desc: 'Saturn in 12th from Moon' };
    if (houseFromMoon === 1) return { name: 'Sade Sati (2nd Phase / Peak)', desc: 'Saturn conjunct Natal Moon' };
    if (houseFromMoon === 2) return { name: 'Sade Sati (3rd Phase / Setting)', desc: 'Saturn in 2nd from Moon' };
    if (houseFromMoon === 4) return { name: 'Shani Dhaiyya (Kantaka / 4th)', desc: 'Saturn in 4th from Moon' };
    if (houseFromMoon === 8) return { name: 'Shani Dhaiyya (Ashtama / 8th)', desc: 'Saturn in 8th from Moon' };
    return { name: 'No Active Sade Sati / Dhaiyya', desc: 'Saturn in supportive transit house from Moon' };
  }, [saturnTransit, satHouseMoon]);

  return (
    <div className="bg-[#FFFDF7] border border-[#B58A4A]/30 rounded-2xl shadow-sm overflow-hidden mb-5">
      {/* Header bar */}
      <div className="bg-[#174C4B]/5 px-4 py-3 border-b border-[#B58A4A]/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Compass className="w-4 h-4 text-[#B58A4A]" />
          <h2 className="font-serif font-bold text-sm text-[#174C4B] tracking-wide">
            {isHi ? 'गोचर त्वरित दृष्टि (Gochar Quick Glance)' : 'Gochar Quick Glance'}
          </h2>
        </div>
        {onNavigateToGochar && (
          <button
            onClick={onNavigateToGochar}
            className="flex items-center gap-1 text-2xs font-semibold text-[#174C4B] hover:text-[#B58A4A] transition-colors"
          >
            <span>{isHi ? 'विस्तृत गोचर' : 'Full Transit View'}</span>
            <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Double Transit Factual Card */}
        <div className="bg-white border border-[#B58A4A]/30 rounded-xl p-3 shadow-2xs">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#B58A4A]" />
              <span className="text-xs font-bold text-[#174C4B] uppercase tracking-wider">
                {isHi ? 'द्वि-गोचर (Double Transit — Guru + Shani)' : 'Double Transit (Guru + Shani)'}
              </span>
            </div>
            <span className="text-2xs font-mono px-2 py-0.5 rounded bg-stone-100 text-stone-600 border border-stone-200">
              Natal Lagna: {lagnaSign}
            </span>
          </div>

          <div className="text-2xs space-y-1.5 text-stone-700">
            <div className="flex items-center justify-between">
              <span>
                <strong className="text-[#174C4B]">Guru (Jupiter):</strong> In H{jupHouseLagna} ({jupiterTransit?.sign}) • Drishti: H{jupAspects.join(', H')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>
                <strong className="text-[#174C4B]">Shani (Saturn):</strong> In H{satHouseLagna} ({saturnTransit?.sign}{saturnTransit?.isRetrograde ? ' ℞' : ''}) • Drishti: H{satAspects.join(', H')}
              </span>
            </div>

            <div className="pt-2 border-t border-stone-100 flex items-center justify-between">
              <span className="font-semibold text-[#174C4B]">
                {isHi ? 'संयुक्त दृष्ट/आरोपित भाव:' : 'Jointly Influenced Houses:'}
              </span>
              {doubleTransitHouses.length > 0 ? (
                <div className="flex items-center gap-1">
                  {doubleTransitHouses.map(h => (
                    <span key={h} className="px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-300 font-bold text-xs">
                      H{h}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-stone-500 italic text-2xs">
                  {isHi ? 'कोई समान भाव दृष्टि संयोग नहीं' : 'No simultaneous target-house overlap'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Live Transit Planets Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {/* Jupiter */}
          {jupiterTransit && (
            <div 
              onClick={() => onSelectPlanet && onSelectPlanet('Jupiter')}
              className={`p-2.5 rounded-xl border transition-all cursor-pointer ${
                highlightedPlanet === 'Jupiter'
                  ? 'bg-amber-50/80 border-[#B58A4A] ring-1 ring-[#B58A4A]'
                  : 'bg-white border-stone-200 hover:border-[#174C4B]/40'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#174C4B]">Jupiter</span>
                {jupiterTransit.isRetrograde && (
                  <span className="text-3xs font-bold text-rose-600 bg-rose-50 px-1 rounded border border-rose-200">℞</span>
                )}
              </div>
              <div className="text-2xs text-stone-600 mt-1">
                {jupiterTransit.sign} {jupiterTransit.formattedDegree}
              </div>
              <div className="text-3xs text-stone-500 mt-0.5">
                H{jupHouseLagna} Lagna • H{jupHouseMoon} Moon
              </div>
            </div>
          )}

          {/* Saturn */}
          {saturnTransit && (
            <div 
              onClick={() => onSelectPlanet && onSelectPlanet('Saturn')}
              className={`p-2.5 rounded-xl border transition-all cursor-pointer ${
                highlightedPlanet === 'Saturn'
                  ? 'bg-amber-50/80 border-[#B58A4A] ring-1 ring-[#B58A4A]'
                  : 'bg-white border-stone-200 hover:border-[#174C4B]/40'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#174C4B]">Saturn</span>
                {saturnTransit.isRetrograde && (
                  <span className="text-3xs font-bold text-rose-600 bg-rose-50 px-1 rounded border border-rose-200">℞</span>
                )}
              </div>
              <div className="text-2xs text-stone-600 mt-1">
                {saturnTransit.sign} {saturnTransit.formattedDegree}
              </div>
              <div className="text-3xs text-stone-500 mt-0.5">
                H{satHouseLagna} Lagna • H{satHouseMoon} Moon
              </div>
            </div>
          )}

          {/* Rahu */}
          {rahuTransit && (
            <div 
              onClick={() => onSelectPlanet && onSelectPlanet('Rahu')}
              className={`p-2.5 rounded-xl border transition-all cursor-pointer ${
                highlightedPlanet === 'Rahu'
                  ? 'bg-amber-50/80 border-[#B58A4A] ring-1 ring-[#B58A4A]'
                  : 'bg-white border-stone-200 hover:border-[#174C4B]/40'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#174C4B]">Rahu</span>
                <span className="text-3xs font-bold text-stone-500 bg-stone-100 px-1 rounded">℞</span>
              </div>
              <div className="text-2xs text-stone-600 mt-1">
                {rahuTransit.sign} {rahuTransit.formattedDegree}
              </div>
              <div className="text-3xs text-stone-500 mt-0.5">
                H{calcHouse(lagnaSignIndex, rahuTransit.signIndex)} Lagna
              </div>
            </div>
          )}

          {/* Ketu */}
          {ketuTransit && (
            <div 
              onClick={() => onSelectPlanet && onSelectPlanet('Ketu')}
              className={`p-2.5 rounded-xl border transition-all cursor-pointer ${
                highlightedPlanet === 'Ketu'
                  ? 'bg-amber-50/80 border-[#B58A4A] ring-1 ring-[#B58A4A]'
                  : 'bg-white border-stone-200 hover:border-[#174C4B]/40'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#174C4B]">Ketu</span>
                <span className="text-3xs font-bold text-stone-500 bg-stone-100 px-1 rounded">℞</span>
              </div>
              <div className="text-2xs text-stone-600 mt-1">
                {ketuTransit.sign} {ketuTransit.formattedDegree}
              </div>
              <div className="text-3xs text-stone-500 mt-0.5">
                H{calcHouse(lagnaSignIndex, ketuTransit.signIndex)} Lagna
              </div>
            </div>
          )}
        </div>

        {/* Shani Sade Sati Status */}
        {sadeSatiPhase && (
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-2.5 flex items-center justify-between text-2xs">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-stone-500" />
              <div>
                <span className="font-semibold text-stone-800">{sadeSatiPhase.name}</span>
                <span className="text-stone-500 ml-1.5">— {sadeSatiPhase.desc}</span>
              </div>
            </div>
            <span className="text-3xs font-mono text-stone-500">Moon in {moonSign}</span>
          </div>
        )}
      </div>
    </div>
  );
};
